import React from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RuleConditionRow from './RuleConditionRow';

const MAX_DEPTH = 5;

const newCondition = (fields) => ({
  kind: 'condition',
  field: fields[0]?.path || 'lead.headcount',
  operator: fields[0]?.allowedOperators?.[0] || 'eq',
  value: null,
});

const newGroup = () => ({ kind: 'group', op: 'AND', children: [] });

export default function RuleGroupRow({
  group,
  onChange,
  onRemove,
  fields,
  industries,
  industryGroups,
  depth,
  isRoot,
}) {
  const setOp = (op) => onChange({ ...group, op });

  const setChild = (idx, next) => {
    const children = [...group.children];
    if (next === null) children.splice(idx, 1);
    else children[idx] = next;
    onChange({ ...group, children });
  };

  const addCondition = () => onChange({ ...group, children: [...group.children, newCondition(fields)] });
  const addGroup = () => onChange({ ...group, children: [...group.children, newGroup()] });

  return (
    <Box
      sx={{
        bgcolor: depth % 2 === 0 ? 'transparent' : (theme) => theme.palette.action.hover,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <ButtonGroup size="small" variant="outlined">
          <Button
            onClick={() => setOp('AND')}
            variant={group.op === 'AND' ? 'contained' : 'outlined'}
          >
            AND
          </Button>
          <Button
            onClick={() => setOp('OR')}
            variant={group.op === 'OR' ? 'contained' : 'outlined'}
          >
            OR
          </Button>
        </ButtonGroup>
        <Typography variant="caption" color="text.secondary">
          {group.children.length} {group.children.length === 1 ? 'condition' : 'conditions'}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {!isRoot && onRemove && (
          <IconButton size="small" onClick={onRemove} title="Remove group">
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      <Stack spacing={1}>
        {group.children.map((child, idx) =>
          child.kind === 'group' ? (
            <RuleGroupRow
              key={idx}
              group={child}
              onChange={(next) => setChild(idx, next)}
              onRemove={() => setChild(idx, null)}
              fields={fields}
              industries={industries}
              industryGroups={industryGroups}
              depth={depth + 1}
            />
          ) : (
            <RuleConditionRow
              key={idx}
              condition={child}
              onChange={(next) => setChild(idx, next)}
              onRemove={() => setChild(idx, null)}
              fields={fields}
              industries={industries}
              industryGroups={industryGroups}
            />
          )
        )}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button size="small" startIcon={<AddIcon />} onClick={addCondition}>
          Add condition
        </Button>
        {depth < MAX_DEPTH - 1 && (
          <Button size="small" startIcon={<LayersOutlinedIcon />} onClick={addGroup}>
            Add group
          </Button>
        )}
      </Stack>
    </Box>
  );
}
